import Checkbox from "immigram-ui-cmon/src/components/Checkbox/Checkbox";
import { DropdownOption } from "immigram-ui-cmon/src/components/Dropdown/Dropdown";
import { useAutoScrollToFieldWithError } from "immigram-ui-cmon/src/hooks/useAutoScrollToFieldWithError";
import clsxm from "immigram-ui-cmon/src/lib/clsxm";
import { Fragment, ReactElement, useMemo, useRef } from "react";
import { useContext } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

import { isAdmin } from "@/lib/auth/rbac";
import { UserContext } from "@/lib/contexts/userContext";
import useTransparentState from "@/lib/hook/useTransparentState";
import { ARRAY_FIELD_NAME } from "@/lib/hookForm";

interface ControlledCheckboxListWithRaisingLabelProps<T extends FieldValues> {
  control: Control<T>;
  index?: number;
  fieldName: string;
  rules?: Record<string, any>;
  options: DropdownOption[];
  disabled?: boolean;
  freeze?: boolean;
  error?: string;
  onChange?: (
    fieldName: string,
    value: string,
    arrayFieldIndex?: number
  ) => void;
  onFocus?: () => void;
  after?: () => ReactElement;
  onBlur?: () => void;
}

const ControlledCheckboxListWithRaisingLabel = <
  T extends Record<string, any> = Record<string, any>
>(
  props: ControlledCheckboxListWithRaisingLabelProps<T>
) => {
  const ctx = useContext(UserContext);

  const canToggleDisabled = props.disabled && isAdmin(ctx.ownUser);
  const [disabledState, setDisabledState] = useTransparentState(props.disabled);

  const groups = useMemo(() => {
    const groups: { [key: string]: any } = {
      defaultGroup: {
        title: "",
        items: [],
      },
    };

    for (const item of props.options) {
      const { group } = item;
      if (!group) {
        groups.defaultGroup.items.push(item);
        continue;
      }

      if (!groups[group]) {
        groups[group] = {
          title: group,
          items: [],
        };
      }

      groups[group].items.push(item);
    }

    return groups;
  }, [props.options]);

  const fieldName = (
    props.index === undefined
      ? props.fieldName
      : `${ARRAY_FIELD_NAME}.${props.index}.${props.fieldName}`
  ) as Path<T>; // TODO: Fix generics for fieldName

  const controlElement = useRef<HTMLDivElement | null>(null);

  useAutoScrollToFieldWithError(
    fieldName,
    controlElement.current,
    !!props.error
  );

  return (
    <div ref={controlElement}>
      <Controller
        control={props.control}
        rules={props.rules}
        name={fieldName}
        render={({ field: { onChange, value = "" } }) => {
          return (
            <>
              {Object.keys(groups).map((key) => (
                <Fragment key={key}>
                  {groups[key].title && (
                    <div className="mb-4 font-semibold text-black-600">
                      {groups[key].title}
                    </div>
                  )}
                  {groups[key].items.map((option: any) => (
                    <div
                      className={clsxm(
                        "mb-4 rounded-2xl border border-gray-100 p-4",
                        !!props.error && "border-2 border-blood-600"
                      )}
                      key={option.value}
                    >
                      <Checkbox
                        description={option.subTitle ?? ""}
                        name={props.fieldName}
                        value={option.value}
                        checked={!!value && value.includes(option.value)}
                        disabled={disabledState}
                        onDisabledToggle={
                          canToggleDisabled ? setDisabledState : undefined
                        }
                        onChange={(x) => {
                          let values = value ? value.split(",") : [];
                          const isChecked = x.target.checked;

                          if (isChecked) {
                            values.push(option.value);
                          } else {
                            values = values.filter((a) => a !== option.value);
                          }

                          onChange(values.join(","));

                          if (props.onChange) {
                            props.onChange(
                              props.fieldName,
                              values.join(","),
                              props.index
                            );
                          }
                        }}
                        label={option.title}
                      />
                    </div>
                  ))}
                </Fragment>
              ))}
            </>
          );
        }}
      />
    </div>
  );
};

export default ControlledCheckboxListWithRaisingLabel;
